function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
import React from "react";
import { Button } from "@progress/kendo-react-buttons";
import { Card, Empty, Search } from "../../components/analytics-kit";
import { Loader, ChartLoader } from "../../components/loaders";
import Error from "../../components/error";
import Chart from "../../components/chart";
import PlunkDropDown from "../../components/plunk-dropdown";
import { num, usd, rangeLabelFor, RANGES, resolveBusinessModel, cleanTitle, titleCase } from "../sales/territory-common";
import { getDemandGaps, getPricingScenario, audienceFromBusinessModel } from "../sales/sales-api";

/*
 * "Demand gaps" grain of the Product Explorer (SALES_FEATURES_REVIEW_2026-10-05 N3;
 * renamed from "Pricing" 2026-10-06, query key stays `pricing` so links keep working).
 * Redesigned 2026-10-09 to the approved Gaps mockup (plain-language rows, summary
 * card, Big gap / Some gap pills); styles in sass/v2/_products-more.scss (pm-).
 *
 * Rails:
 *   GET /v1/sales/pricing/opportunities/{range} → styles viewed far more than
 *       ordered, with the price, interest, sales and the reason fields; optional
 *       suggestedPrice from the elasticity model. The envelope's siteAudience
 *       (consumer | trade) drives the copy; priceSignal gates the what-if.
 *   GET /v1/sales/pricing/scenario/{productId}?price= → the elasticity curve
 *       (price → units, revenue) with a lowSignal flag; the drawer's slider
 *       reads it as a what-if.
 *
 * These are the tenant's OWN prices (never ad spend), so managed tenants see
 * them too. Both calls are fail-soft.
 *
 * Gap tiers: the rail ranks rows by gap (gapRank, else gap, else views per unit
 * ordered). The top third of that ranking is "Big gap", the rest "Some gap".
 * Thirds keep the split meaningful at any list size and any gap scale (the gap
 * score has no fixed units across tenants, so a fixed threshold would not).
 */

const money = v => v == null ? "-" : usd(v);
// No cents in headlines: $1,203 / $17K / $1.2M.
function whole(v) {
  if (v == null || !Number.isFinite(Number(v))) return "-";
  const n = Number(v);
  const a = Math.abs(n);
  if (a >= 1e6) return `$${(n / 1e6).toFixed(1).replace(/\.0$/, "")}M`;
  if (a >= 1e4) return `$${Math.round(n / 1e3).toLocaleString("en-US")}K`;
  return `$${Math.round(n).toLocaleString("en-US")}`;
}
function displayTitle(t) {
  const v = cleanTitle(t || "") || "";
  return /[A-Z]/.test(v) && v === v.toUpperCase() ? titleCase(v) : v;
}
function about(units) {
  const u = Number(units);
  if (!Number.isFinite(u)) return "-";
  if (u > 0 && u < 1) return "fewer than 1";
  return `about ${Math.round(u).toLocaleString("en-US")}`;
}

// Linear interpolation on the curve; outside the sampled range the ends hold.
function curveAt(points, price) {
  if (!points || !points.length) return null;
  if (price <= points[0].price) return points[0];
  const last = points[points.length - 1];
  if (price >= last.price) return last;
  for (let i = 1; i < points.length; i++) {
    const a = points[i - 1];
    const b = points[i];
    if (price <= b.price) {
      const t = b.price === a.price ? 0 : (price - a.price) / (b.price - a.price);
      const units = a.units != null && b.units != null ? a.units + (b.units - a.units) * t : null;
      // Revenue is units times price (interpolating revenue separately could disagree with the units shown).
      const revenue = units != null ? units * price : null;
      return {
        price,
        units,
        revenue
      };
    }
  }
  return last;
}

// "20% lower" / "15% higher" / "today's price"
function vsNow(price, base) {
  const c = Number(price);
  const b = Number(base);
  if (!Number.isFinite(c) || !Number.isFinite(b) || b === 0) return "";
  const p = Math.round((c - b) / b * 100);
  if (p === 0) return "today's price";
  return `${Math.abs(p)}% ${p < 0 ? "lower" : "higher"}`;
}
export class ScenarioDrawer extends React.Component {
  constructor(props) {
    super(props);
    _defineProperty(this, "onKey", e => {
      if (e.key === "Escape") this.props.onClose();
    });
    // Always ask the model for the exact price (debounced): the sampled points only
    // cover -20% to the cluster median, and interpolating clamps outside that band.
    _defineProperty(this, "setPrice", v => {
      const price = Math.round(Number(v) * 100) / 100;
      this.setState({
        price,
        busy: true
      });
      clearTimeout(this._t);
      this._t = setTimeout(async () => {
        if (!this._alive) return;
        this.setState({
          busy: true
        });
        try {
          const r = await getPricingScenario(this.props.item.productId, price, this.props.range);
          const custom = r && r.points ? r.points.find(x => x.label === "custom") || r.points.find(x => Math.abs(x.price - price) < 1) : null;
          if (this._alive && this.state.price === price) this.setState({
            at: custom || r && r.at || null,
            busy: false
          });else if (this._alive) this.setState({
            busy: false
          });
        } catch (e) {
          if (this._alive) this.setState({
            busy: false
          });
        }
      }, 150);
    });
    this.state = {
      scenario: undefined,
      price: null,
      at: null,
      busy: false
    };
    this._alive = true;
    this._t = null;
  }
  componentDidMount() {
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", this.onKey);
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
    clearTimeout(this._t);
    document.body.style.overflow = "";
    window.removeEventListener("keydown", this.onKey);
  }
  async load() {
    const {
      item
    } = this.props;
    try {
      const sc = await getPricingScenario(item.productId, null, this.props.range);
      if (!this._alive) return;
      const base = (sc && sc.currentPrice) != null ? sc.currentPrice : item.price;
      this.setState({
        scenario: sc,
        price: base != null ? Math.round(base * 100) / 100 : null,
        at: sc && sc.at
      });
    } catch (e) {
      if (this._alive) this.setState({
        scenario: null,
        price: item.price != null ? item.price : null
      });
    }
  }
  chart(points) {
    if (!points || points.length < 2) return null;
    const data = [["Price", "Revenue", "Units"]];
    points.forEach(p => data.push([usd(p.price), Math.round((Number(p.revenue) || 0) * 100) / 100, Math.round((Number(p.units) || 0) * 10) / 10]));
    return data;
  }
  render() {
    const {
      item,
      range,
      onClose,
      history
    } = this.props;
    const {
      scenario,
      price,
      at,
      busy
    } = this.state;
    const base = scenario && scenario.currentPrice != null ? scenario.currentPrice : item.price;
    const points = (scenario && scenario.points || []).filter(x => x.label !== "custom");
    const baseline = base != null ? points.find(x => x.label === "current") || curveAt(points, base) || null : null;
    const exact = at && price != null && Math.abs(at.price - price) < 1 ? at : null; // the model prices to the whole dollar
    const chosen = exact || (points.length >= 2 && price != null ? curveAt(points, price) : at);
    const med = scenario && scenario.clusterMedianPrice ? scenario.clusterMedianPrice : 0;
    const min = base ? Math.max(0.01, Math.floor(base * 0.5)) : 0;
    const max = base ? Math.ceil(Math.max(base * 2, med * 2)) : 100;
    const step = base ? Math.max(0.01, Math.round(base / 100 * 100) / 100) : 1;
    const chart = scenario && scenario.notEnoughSales ? null : this.chart(points);
    const label = [item.productId, displayTitle(item.title)].filter(Boolean).join(" · ");
    const period = rangeLabelFor(range, RANGES);
    const sliderPct = price != null && max > min ? Math.max(0, Math.min(100, (price - min) / (max - min) * 100)) : 0;
    return /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("div", {
      className: "mi-drawer-backdrop",
      onClick: onClose,
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("aside", {
      className: "mi-drawer pm-drawer",
      role: "dialog",
      "aria-label": "Try other prices"
    }, /*#__PURE__*/React.createElement("header", {
      className: "mi-drawer-header"
    }, /*#__PURE__*/React.createElement("div", {
      className: "mi-drawer-title"
    }, /*#__PURE__*/React.createElement("h2", null, "Try other prices"), /*#__PURE__*/React.createElement("div", {
      className: "mi-drawer-subtitle"
    }, label)), /*#__PURE__*/React.createElement("div", {
      className: "mi-drawer-actions"
    }, /*#__PURE__*/React.createElement(Button, {
      fillMode: "flat",
      size: "small",
      onClick: () => history && history.push(`/Products/${range}/product/${encodeURIComponent(item.productId)}`)
    }, "Product detail"), /*#__PURE__*/React.createElement(Button, {
      fillMode: "flat",
      size: "small",
      onClick: onClose
    }, "Close"))), /*#__PURE__*/React.createElement("div", {
      className: "mi-drawer-body"
    }, scenario === undefined ? /*#__PURE__*/React.createElement(Loader, null) : scenario === null ? /*#__PURE__*/React.createElement("p", {
      className: "pm-muted",
      style: {
        margin: 0
      }
    }, "We can't test prices for this product yet. It needs to have sold at a few different prices first.") : /*#__PURE__*/React.createElement(React.Fragment, null, /*#__PURE__*/React.createElement("p", {
      className: "pm-lead"
    }, "Move the price to see what might happen, based on how your other similar products sell at different prices."), scenario.notEnoughSales ? /*#__PURE__*/React.createElement("p", {
      className: "pm-note warn"
    }, "Not enough sales to estimate. This product has too few orders to say how many it might sell ", period.toLowerCase(), " at another price. Try a longer date range.") : scenario.lowSignal ? /*#__PURE__*/React.createElement("p", {
      className: "pm-note warn"
    }, "This product has only sold at a few prices, so treat these numbers as a starting point for a test, not a forecast.") : null, /*#__PURE__*/React.createElement("div", {
      className: "pm-slider"
    }, /*#__PURE__*/React.createElement("span", null, whole(min)), /*#__PURE__*/React.createElement("input", {
      type: "range",
      min: min,
      max: max,
      step: step,
      value: price != null ? price : base || 0,
      onChange: e => this.setPrice(e.target.value),
      style: {
        "--pm-fill": `${sliderPct}%`
      },
      "aria-label": "Price"
    }), /*#__PURE__*/React.createElement("span", null, whole(max)), /*#__PURE__*/React.createElement("input", {
      className: "pm-price-in",
      type: "number",
      min: 0,
      step: 1,
      value: price != null ? Math.round(price) : "",
      onChange: e => this.setPrice(e.target.value),
      "aria-label": "Price in dollars"
    })), /*#__PURE__*/React.createElement("p", {
      className: "pm-muted pm-small"
    }, "Sells for ", whole(base), " today", item.suggestedPrice != null ? ` · our suggestion is ${whole(item.suggestedPrice)}` : "", med ? ` · similar products sell for about ${whole(med)}` : ""), /*#__PURE__*/React.createElement("div", {
      className: `pm-whatif${busy ? " busy" : ""}`,
      "aria-busy": busy
    }, /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, "At"), /*#__PURE__*/React.createElement("b", null, whole(price)), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, vsNow(price, base))), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, "Might sell"), /*#__PURE__*/React.createElement("b", null, scenario.notEnoughSales ? "Not enough sales to estimate" : chosen && chosen.units != null ? about(chosen.units) : busy ? "…" : "-"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, period, baseline && baseline.units != null ? ` · from ${about(baseline.units).replace("about ", "")} now` : "")), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("span", {
      className: "l"
    }, "Revenue"), /*#__PURE__*/React.createElement("b", null, scenario.notEnoughSales ? "-" : chosen && chosen.revenue != null ? `about ${whole(chosen.revenue)}` : busy ? "…" : "-"), /*#__PURE__*/React.createElement("span", {
      className: "s"
    }, "a rough guide, not a promise")), busy ? /*#__PURE__*/React.createElement("span", {
      className: "pm-updating"
    }, "Updating") : null), chart ? /*#__PURE__*/React.createElement(Card, {
      title: "How sales change with price",
      hint: "revenue bars \xB7 units line"
    }, /*#__PURE__*/React.createElement("div", {
      className: "territory-trend"
    }, /*#__PURE__*/React.createElement(Chart, {
      width: "100%",
      height: "240px",
      chartType: "ComboChart",
      loader: /*#__PURE__*/React.createElement(ChartLoader, null),
      data: chart,
      options: {
        seriesType: "bars",
        series: {
          0: {
            targetAxisIndex: 0,
            color: "#ff6358",
            tooltipFormat: "currency"
          },
          1: {
            type: "line",
            targetAxisIndex: 1,
            color: "#78d237",
            tooltipFormat: "{0:n0}"
          }
        },
        vAxes: {
          0: {
            title: "Revenue",
            format: "short"
          },
          1: {
            title: "Units",
            format: "#,##0.#"
          }
        },
        legend: {
          position: "top"
        }
      }
    }))) : null, /*#__PURE__*/React.createElement("p", {
      className: "pm-muted pm-small"
    }, "These guesses come from your own order history for ", period.toLowerCase(), " and assume the same number of people keep looking. Sales, promotions and times it was out of stock can move them.")))));
  }
}
export const AUDIENCE_HINT = {
  trade: "what designers and dealers browse against what dealers order, by product and category",
  consumer: "what shoppers view against what they buy, by product and category"
};
export const NO_PRICE_SIGNAL = "This product's price hardly changes, so the effect of a new price is hard to predict. Look at photos, options or lead time first.";
const SORTS = [["gap", "Biggest gap"], ["views", "Most views"], ["units", "Fewest units"], ["revenue", "Revenue"], ["price", "Highest price"]];
const priceHigh = r => r.pricePosition != null ? r.pricePosition >= 1.15 : /priced\s+\d+%\s+above/i.test(String(r.reason || ""));

// Plain sentences from the row fields.
function whySentence(r) {
  const views = num(Math.round(r.views || 0));
  const units = Math.round(Number(r.units) || 0);
  const first = units === 0 ? `Viewed ${views} times with no orders.` : `Viewed ${views} times, ${num(units)} ordered.`;
  let second = "";
  const pos = r.pricePosition;
  const conv = r.categoryConversionPer100;
  if (r.stockoutSuspect) second = "It may be out of stock or not orderable.";else if (pos != null && pos >= 2) second = "Priced more than double similar products.";else if (pos != null && pos >= 1.15) second = `Priced ${Math.round((pos - 1) * 100)}% above similar products.`;else if (conv != null && conv > 0) {
    const c = conv >= 1 ? Math.round(conv) : Math.round(conv * 10) / 10;
    second = `Similar products sell about ${c} for every 100 people who look.`;
  } else if (pos == null && priceHigh(r)) {
    const m = String(r.reason).match(/priced\s+(\d+)%\s+above/i);
    second = m ? `Priced ${m[1]}% above similar products.` : "";
  } else if (r.priceSignal === false) second = "Its price hardly changes, so look at photos, options or lead time first.";else second = "Fewer people order it than similar products.";
  return `${first} ${second}`.trim();
}
export default class PricingGrain extends React.Component {
  constructor(props) {
    super(props);
    this.state = {
      env: undefined,
      error: false,
      q: "",
      sort: "gap",
      tier: "all",
      open: null,
      bm: null
    };
    this._alive = true;
  }
  componentDidMount() {
    this.load();
  }
  componentWillUnmount() {
    this._alive = false;
  }
  componentDidUpdate(prev) {
    if (prev.range !== this.props.range) {
      this.setState({
        env: undefined,
        error: false
      }, () => this.load());
    }
  }
  async load() {
    try {
      // Audience default when the envelope is silent: trade for manufacturers, consumer otherwise.
      const bm = this.state.bm || (await resolveBusinessModel().catch(() => "both"));
      const env = await getDemandGaps(this.props.range, {
        audienceDefault: audienceFromBusinessModel(bm)
      });
      if (this._alive) this.setState({
        env,
        bm
      });
    } catch (e) {
      if (this._alive) this.setState({
        error: true
      });
    }
  }

  // Big gap = top third of the rail's own ranking (gapRank, else gap, else views per unit).
  tiers(items) {
    const score = r => r.gapRank != null ? -r.gapRank : r.gap != null ? r.gap : (Number(r.views) || 0) / ((Number(r.units) || 0) + 1);
    const ranked = items.map((r, i) => [i, score(r)]).sort((a, b) => b[1] - a[1]);
    const bigN = Math.ceil(items.length / 3);
    const big = new Set(ranked.slice(0, bigN).map(([i]) => i));
    const order = new Map(ranked.map(([i], k) => [i, k]));
    return items.map((r, i) => ({
      ...r,
      tier: big.has(i) ? "big" : "some",
      gapOrder: order.get(i)
    }));
  }
  render() {
    const {
      range,
      history
    } = this.props;
    const {
      env,
      error,
      q,
      open,
      sort,
      tier
    } = this.state;
    if (error) return /*#__PURE__*/React.createElement(Error, {
      onRetry: () => this.setState({
        env: undefined,
        error: false
      }, () => this.load())
    });
    if (env === undefined) return /*#__PURE__*/React.createElement(Loader, null);
    if (env === null) {
      return /*#__PURE__*/React.createElement("div", {
        className: "wa-grid"
      }, /*#__PURE__*/React.createElement(Card, {
        title: "Demand gaps"
      }, /*#__PURE__*/React.createElement("p", {
        className: "pm-muted",
        style: {
          margin: 0
        }
      }, "Demand gaps aren't ready for this account yet. They appear once Plunk has a few weeks of website visits and orders to compare.")));
    }
    const trade = env.siteAudience === "trade";
    const items = this.tiers(env.items || []);
    const bigCount = items.filter(r => r.tier === "big").length;
    const views = items.reduce((a, r) => a + (Number(r.views) || 0), 0);
    const units = items.reduce((a, r) => a + (Number(r.units) || 0), 0);
    const pricey = items.filter(priceHigh).length;
    const term = q.trim().toLowerCase();
    const sorter = {
      gap: (a, b) => a.gapOrder - b.gapOrder,
      views: (a, b) => (b.views || 0) - (a.views || 0),
      units: (a, b) => (a.units || 0) - (b.units || 0),
      revenue: (a, b) => (b.revenue || 0) - (a.revenue || 0),
      price: (a, b) => (b.price || 0) - (a.price || 0)
    }[sort] || ((a, b) => a.gapOrder - b.gapOrder);
    const rows = items.filter(r => tier === "all" || r.tier === tier).filter(r => !term || String(r.title || "").toLowerCase().includes(term) || String(r.productId).toLowerCase().includes(term)).sort(sorter);
    const pills = [["all", "All", items.length], ["big", "Big gap", bigCount], ["some", "Some gap", items.length - bigCount]];
    return /*#__PURE__*/React.createElement(React.Fragment, null, items.length ? /*#__PURE__*/React.createElement("section", {
      className: "pm-summary"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-dot",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("div", null, /*#__PURE__*/React.createElement("b", null, num(items.length), " product", items.length === 1 ? "" : "s", " ", items.length === 1 ? "gets" : "get", " looked at far more than ", items.length === 1 ? "it gets" : "they get", " ordered"), /*#__PURE__*/React.createElement("span", null, items.length === 1 ? "It" : "Together they", " had ", num(Math.round(views)), " views on your website and ", num(Math.round(units)), " unit", Math.round(units) === 1 ? "" : "s", " ordered", trade ? " by dealers" : "", ".", pricey > items.length / 2 ? " Most are priced well above similar products, so price is the first thing to check." : ""))) : null, /*#__PURE__*/React.createElement("div", {
      className: "wa-grid"
    }, /*#__PURE__*/React.createElement("section", {
      className: "wa-card c12 pm-gaps"
    }, /*#__PURE__*/React.createElement("div", {
      className: "wa-toolbar pm-toolbar"
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-pills",
      role: "tablist"
    }, pills.map(([k, l, n]) => /*#__PURE__*/React.createElement("button", {
      key: k,
      role: "tab",
      "aria-selected": tier === k,
      className: tier === k ? "on" : "",
      onClick: () => this.setState({
        tier: k
      })
    }, l, " ", num(n)))), /*#__PURE__*/React.createElement("span", {
      className: "pm-grow"
    }), /*#__PURE__*/React.createElement(Search, {
      value: q,
      onChange: v => this.setState({
        q: v
      }),
      placeholder: "Search by name or SKU"
    }), /*#__PURE__*/React.createElement(PlunkDropDown, {
      width: "190px",
      value: sort,
      onChange: v => this.setState({
        sort: v
      }),
      options: SORTS.map(([k, l]) => [k, `Sort: ${l}`])
    })), rows.length ? /*#__PURE__*/React.createElement("div", {
      className: "pm-table pm-gap-rows",
      role: "table"
    }, /*#__PURE__*/React.createElement("div", {
      className: "pm-th",
      role: "row"
    }, /*#__PURE__*/React.createElement("span", null, "Product"), /*#__PURE__*/React.createElement("span", null, "Gap"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Views"), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, "Units"), /*#__PURE__*/React.createElement("span", null, "Why it may not be selling"), /*#__PURE__*/React.createElement("span", null)), rows.map(r => /*#__PURE__*/React.createElement("div", {
      className: "pm-tr",
      role: "row",
      key: r.productId
    }, /*#__PURE__*/React.createElement("span", {
      className: "pm-style"
    }, r.imageUrl ? /*#__PURE__*/React.createElement("img", {
      className: "pm-thumb wide",
      src: r.imageUrl,
      alt: "",
      loading: "lazy",
      onError: e => {
        e.target.style.visibility = "hidden";
      }
    }) : /*#__PURE__*/React.createElement("span", {
      className: "pm-thumb wide",
      "aria-hidden": "true"
    }), /*#__PURE__*/React.createElement("span", {
      className: "pm-stack"
    }, /*#__PURE__*/React.createElement("b", null, [r.productId, displayTitle(r.title)].filter(Boolean).join(" · ")), r.price != null ? /*#__PURE__*/React.createElement("span", {
      className: "pm-sub"
    }, "Sells for ", whole(r.price)) : null)), /*#__PURE__*/React.createElement("span", null, /*#__PURE__*/React.createElement("span", {
      className: `pm-chip ${r.tier}`
    }, r.tier === "big" ? "Big gap" : "Some gap")), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(Math.round(r.views || 0))), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, num(Math.round(r.units || 0))), /*#__PURE__*/React.createElement("span", {
      className: "pm-why"
    }, whySentence(r)), /*#__PURE__*/React.createElement("span", {
      className: "n"
    }, r.priceSignal === false ? /*#__PURE__*/React.createElement("span", {
      className: "pm-muted pm-small",
      title: NO_PRICE_SIGNAL
    }, "Price isn't the issue") : /*#__PURE__*/React.createElement("a", {
      href: "#",
      className: "pm-link",
      onClick: e => {
        e.preventDefault();
        this.setState({
          open: r
        });
      }
    }, "Try other prices"))))) : /*#__PURE__*/React.createElement(Empty, null, term ? "Nothing matches that search." : items.length ? "Nothing in this group." : "No demand gaps in this date range. Everything people look at is getting ordered."), /*#__PURE__*/React.createElement("p", {
      className: "pm-foot"
    }, /*#__PURE__*/React.createElement("span", null, "A demand gap is a product people look at a lot on your website but rarely order, compared with similar products. \"Try other prices\" estimates what might happen at a higher or lower price, from your own order history.")))), open ? /*#__PURE__*/React.createElement(ScenarioDrawer, {
      key: `${range}|${open.productId}`,
      item: open,
      range: range,
      history: history,
      onClose: () => this.setState({
        open: null
      })
    }) : null);
  }
}